import React, { useState } from 'react';

interface BatchInfoData {
    batchNumber: string;
    expirationDate: string;
    productionDate: string;
}

interface Article {
    id: string;
    name: string;
    description?: string;
}

interface BatchInfoPopupProps {
    open: boolean;
    onClose: () => void;
    selectedArticle?: Article | null;
    onSave?: (data: BatchInfoData) => void;
}

const BatchInfoPopup: React.FC<BatchInfoPopupProps> = ({
    open,
    onClose,
    selectedArticle,
    onSave
}) => {
    const [formData, setFormData] = useState<BatchInfoData>({
        batchNumber: '',
        expirationDate: '',
        productionDate: ''
    });

    const [errors, setErrors] = useState<Partial<BatchInfoData>>({});

    const handleInputChange = (field: keyof BatchInfoData) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<BatchInfoData> = {};

        if (!formData.batchNumber.trim()) {
            newErrors.batchNumber = 'Chargennummer ist erforderlich';
        }

        // if (!formData.expirationDate) {
        //     newErrors.expirationDate = 'Mindesthaltbarkeitsdatum ist erforderlich';
        // }

        // if (!formData.productionDate) {
        //     newErrors.productionDate = 'Herstellungsdatum ist erforderlich';
        // }

        // // Validate that expiration date is after production date
        // if (formData.expirationDate && formData.productionDate) {
        //     const prodDate = new Date(formData.productionDate);
        //     const expDate = new Date(formData.expirationDate);

        //     if (expDate <= prodDate) {
        //         newErrors.expirationDate = 'MHD muss nach dem Herstellungsdatum liegen';
        //     }
        // }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (validateForm()) {
            if(onSave){
                onSave(formData);
            }
            handleClose();
        }
    };

    const handleClose = () => {
        setFormData({
            batchNumber: '',
            expirationDate: '',
            productionDate: ''
        });
        setErrors({});
        onClose();
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b">
                    <h2 className="text-xl font-semibold text-gray-900">
                        Chargeninformationen eingeben
                    </h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Selected Article Info */}
                    {selectedArticle && (
                        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="text-sm font-medium text-blue-800 mb-1">
                                Ausgewählter Artikel:
                            </div>
                            <div className="font-semibold text-gray-900">
                                {selectedArticle.name}
                            </div>
                            {selectedArticle.description && (
                                <div className="text-sm text-gray-600 mt-1">
                                    {selectedArticle.description}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Form Fields */}
                    <div className="space-y-4">
                        {/* Chargennummer */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Chargennummer *
                            </label>
                            <input
                                type="text"
                                value={formData.batchNumber}
                                onChange={handleInputChange('batchNumber')}
                                placeholder="z.B. CH-2024-001"
                                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.batchNumber ? 'border-red-500' : 'border-gray-300'
                                    }`}
                            />
                            {errors.batchNumber && (
                                <p className="mt-1 text-sm text-red-600">{errors.batchNumber}</p>
                            )}
                        </div>

                        {/* Herstellungsdatum */}
                        {/* <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Herstellungsdatum *
                            </label>
                            <input
                                type="date"
                                value={formData.productionDate}
                                onChange={handleInputChange('productionDate')}
                                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.productionDate ? 'border-red-500' : 'border-gray-300'
                                    }`}
                            />
                            {errors.productionDate && (
                                <p className="mt-1 text-sm text-red-600">{errors.productionDate}</p>
                            )}
                        </div> */}

                        {/* Mindesthaltbarkeitsdatum */}
                        {/* <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Mindesthaltbarkeitsdatum *
                            </label>
                            <input
                                type="date"
                                value={formData.expirationDate}
                                onChange={handleInputChange('expirationDate')}
                                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.expirationDate ? 'border-red-500' : 'border-gray-300'
                                    }`}
                            />
                            {errors.expirationDate && (
                                <p className="mt-1 text-sm text-red-600">{errors.expirationDate}</p>
                            )}
                        </div> */}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-lg">
                    <button
                        onClick={handleClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Abbrechen
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Speichern
                    </button>
                </div>
            </div>
        </div>
    );
};


export default BatchInfoPopup;